// @ts-strict-ignore
import { BehaviorSubject, Subject, Subscription, filter } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { Webgl2Pipeline } from 'rainbow-web-sdk/services/virtualBackground/pipeline/webgl2/webgl2Pipeline';
import { Canvas2DPipeline } from 'rainbow-web-sdk/services/virtualBackground/pipeline/canvas2D/canvas2DPipeline';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { getBinaryData } from 'rainbow-web-sdk/services/core/imageUtils.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { Service } from 'rainbow-web-sdk/services/service';
import createTFLiteModule from 'rainbow-web-sdk/services/virtualBackground/common/tflite';
import createTFLiteSIMDModule from 'rainbow-web-sdk/services/virtualBackground/common/tflite-simd';
import { CLEAR_TIMEOUT, SET_TIMEOUT, TIMEOUT_TICK, makeTimerWorkerScript } from 'rainbow-web-sdk/services/virtualBackground/common/TimerWorker';

declare let WebAssembly: any;

export const VIRTUAL_BACKGROUND_SVC: string = 'VirtualBackgroundService';

/**
 * @module VirtualBackground
 * This service manages the statistics of the virtual background
 */

export const glsl = String.raw;
export const segmentationDimensions = { model96: { height: 96, width: 160 }, model144: { height: 144, width: 256 } };
export const VIRTUAL_BACKGROUND_TYPE = { IMAGE: 'image', BLUR: 'blur', BLUR_PLUS: 'blur-plus', NONE: 'none', VIDEO: 'video' };

export interface Dimensions {
    height: number;
    width: number;
}

export interface Options {
    backgroundType: string;
    imgBg?: string;
    stream?: MediaStream;
    segmentationDimensions?: Dimensions;
}

export interface IVideoPipeline {
    init(): void;
    render(): void;
    cleanUp(): void;
    updatePostProcessingConfig(): void;
}

/**
 * @eventProperty
 */
export enum VirtualBackgroundServiceEvents {
    /**
     * @eventProperty
     * This `RBEvent` is sent when the virtuallBackgroundService stream is updated
     */
    UPDATE_STREAM = 'UPDATE_STREAM',

    /**
    * @eventProperty
    * This `RBEvent` is sent when the virtuallBackgroundService mirror style is updated
    */
    UPDATE_STREAM_MIRROR = 'UPDATE_STREAM_MIRROR'
}

export interface VirtualBackgroundService {

    /**
     * Subscribe to updates from the service (all events are of RBEvent type
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - Array of event to listen
     */
    start(): Promise<void>;
    stop(): Promise<void>;
    reconnect(): Promise<void>;
    subscribe(handler: (event: RBEvent<VirtualBackgroundServiceEvents>) => any, eventNames?: VirtualBackgroundServiceEvents | VirtualBackgroundServiceEvents[]): Subscription;
    startProcessStream(stream: MediaStream, serviceName?: string): Promise<MediaStream>;
    stopProcessStream(): Promise<void>;
    setBackgroundMode(mode: string, url?: string, stream?: MediaStream): Promise<void>;
    setVideoAppearance(rect: number[]);
    getBackgroundMode(): any;
    setMirror(mirror: boolean): void;
    getMirror(): boolean;
    setFramerate(framerate: number): void;

    putAdminBackgroundImage(backgroundCompany: any): Promise<void>;
    deleteAdminBackgroundImage(backgroundIdAdmin: string): Promise<void>;

    putPersonnalBackgroundImage(backgroundUser: any, loadBgUrl: string): Promise<void>;
    deletePersonnalBackgroundImage(backgroundIdUser: string): Promise<void>;
    authorized(): boolean;
    backgroundImages: BehaviorSubject<any>;
    loadBg: BehaviorSubject<any>;
}

/** @internal  */
export class VirtualBackgroundServiceRB extends Service implements VirtualBackgroundService {

    private settingsService: SettingsService = SettingsService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private videoPipeline: IVideoPipeline | null = null;

    public statsEnabledFlag: boolean = false;
    private statsInterval: any = null;

    private started: boolean = false;
    private stream: MediaStream;
    private modifiedStream: MediaStream;

    public options: Options;
    public segmentationPixelCount: number;
    public tflite: any;
    public backgroundImages: BehaviorSubject<any>;
    public loadBg: BehaviorSubject<any>;

    public gl: any;
    public segmentationMaskCanvas: HTMLCanvasElement;
    private isStreamMirrored: boolean = true;
    public simdSupported: boolean = false;
    public segmentationTexture: WebGLTexture;
    public personMaskTexture: WebGLTexture;

    private xmppSubscription: any = null;
    public outputCanvasElement: HTMLCanvasElement | null = null;
    public videoElement: HTMLVideoElement | null = null;
    public testVideo: HTMLVideoElement | null = null;
    public ctx: any;
    public maskFrameTimerWorker: Worker;
    public virtualImage: CanvasImageSource;
    public virtualVideo: CanvasImageSource;
    public rxSubject: Subject<any> = new Subject<any>();
    public texture: any;

    public vertexShader: WebGLShader;
    public outputMemoryOffset: number;
    public webglFlag: boolean = false;
    public webglAllowed: boolean = false;
    public sigmaColor: any;
    public sigmaSpace: any;
    public coverageMax: any;
    public coverageMin: any;
    public lightWrapping: any;
    public blendMode: any;

    private models: any = {
        model96: '/cacheV2/virtual-background/models/segm_lite_v681.tflite',
        model144: '/cacheV2/virtual-background/models/segm_full_v679.tflite'
    };

    private settings: any = {
        showVideoBackground: 'false',
        webGLAllowed: 'false',
        sigmaSpace: '1',
        sigmaColor: '0.1',
        coverageMax: '0.9',
        coverageMin: '0.3',
        lightWrapping: '0.5',
        blendMode: '0.6'
    };

    private tasksCanvas: OffscreenCanvas = new OffscreenCanvas(1, 1);

    private webglSubscription: Subscription = null;

    public static getInstance() { return Service.create(VIRTUAL_BACKGROUND_SVC, VirtualBackgroundServiceRB); }
    public static build() { return new VirtualBackgroundServiceRB(); }

    private constructor() {
        super();

        // Check if WASM is available
        if (!this.wasmAvailable()) {
            this.settingsService.setSetting('virtualBackgroundFlag', 'false');
            this.logger.warn('[VirtualBackgroundService] NOT AVAILABLE -- REASON : ENHANCED BROWSER SECUITY ACTIVATED FOR THIS SITE');
            return;
        }
        this.settingsService.setSetting('virtualBackgroundFlag', 'true');

        // Check if mathematic co-processor is available
        this.simdSupported = this.simdAvailable();

        this.videoElement = document.createElement('video');
        this.webglAvailable();

        // Get virtualBackground data
        this.isStreamMirrored = localStorage.getItem('virtualBackgroundMirror') === 'true';
        this.backgroundImages = new BehaviorSubject<any>({ companyBackgrounds: [], userBackgrounds: [] });
        this.loadBg = new BehaviorSubject<any>({ loadPersonalBg: [] });

    }

    public authorized(): boolean {
        return this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.BLURRING_UI_2_0) && this.settingsService.getSetting("virtualBackgroundFlag") === "true";
    }

    public subscribe(handler: (event: RBEvent<VirtualBackgroundServiceEvents>) => any, eventNames?: VirtualBackgroundServiceEvents | VirtualBackgroundServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<VirtualBackgroundServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<VirtualBackgroundServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[VirtualBackgroundService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /****************************************************************************/
    /** SERVICE LIFECYCLE                                                      */
    /****************************************************************************/

    /**
     * This async method has to be call to start the virtual Background Service
     */
    public async start(): Promise<void> {
        this.logger.info(`[VirtualBackgroundService] === Start === ${this.webglAllowed ? 'webgl' : 'canvas2d'} pipeline == simd ${this.simdSupported ? 'supported' : 'not supported'}`);
        this.backgroundImages.next(await this.getBackgrounds());
        this.backgroundImages.value.companyBackgrounds.reverse();
        this.backgroundImages.value.userBackgrounds.reverse();
        this.loadBg.next({ loadPersonalBg: [] });
        this.xmppSubscription = this.xmppService.addHandler((stanza: any) => { this.onXmppEvent(stanza); return true; }, 'jabber:iq:configuration', 'message', 'management');
    }

    /**
     * This async method has to be call to stop the virtual Background Service
     */
    public async stop(): Promise<void> {
        this.logger.info('[VirtualBackgroundService] === Stop ===');
        this.backgroundImages.next({ companyBackgrounds: [], userBackgrounds: [] });
        this.loadBg.next({ loadPersonalBg: [] });
        this.cleanUp();
        if (this.xmppSubscription) { this.xmppService.deleteHandler(this.xmppSubscription); this.xmppSubscription = null; }
        if (this.webglSubscription) { this.webglSubscription.unsubscribe(); }
    }

    /**
     * This async method has to be call to reconnect the virtual Background Service
     */
    public async reconnect(): Promise<void> {
        this.logger.info('[VirtualBackgroundService] === Reconnect ===');
        this.cleanUp();
        this.backgroundImages.next(await this.getBackgrounds());
        this.loadBg.next({ loadPersonalBg: [] });
        if (this.xmppSubscription) { this.xmppService.deleteHandler(this.xmppSubscription); this.xmppSubscription = null; }
        this.xmppSubscription = this.xmppService.addHandler((stanza: any) => { this.onXmppEvent(stanza); return true; }, 'jabber:iq:configuration', 'message', 'management');
    }

    public setVideoAppearance(_rect: number[]): void {}

    /**
     * XMPP room event handler
     * @param stanza - the xmpp stanza event handler
     */
    private onXmppEvent(stanza: any): boolean {
        try {
            const stanzaElem = $(stanza);
            const backgroundElem = stanzaElem.find('background');
            const backgroundId = backgroundElem.attr('backgroundId');
            const action = backgroundElem.attr('action');
            const type = backgroundElem.attr('type');

            switch (action) {
                case 'create':
                    this.logger.info(`[virtualBackgroundService] onXmppEvent -- ${action} `);
                    setTimeout(() => {
                        if (type === 'userBackgrounds') { this.loadBg.next({ loadPersonalBg: [] }); }
                        this.backgroundImages.value[type].unshift({ id: backgroundId });
                        this.backgroundImages.next(this.backgroundImages.value);
                    }, 1);
                    break;

                case 'delete':
                    this.logger.info(`[virtualBackgroundService] onXmppEvent -- ${action} `);
                    this.backgroundImages.value[type].splice(this.backgroundImages.value[type].map(el => el.id).indexOf(backgroundId), 1);
                    this.backgroundImages.next(this.backgroundImages.value);
                    break;

                default:
                    break;
            }
        }
        catch (error) {
            this.logger.error("[VirtualBackgroundService] onXmppEvent -- failure -- " + error.message);
        }
        return true;
    }

    /****************************************************************************/
    /** PUBLIC STUFF                                                           */
    /****************************************************************************/

    /**
     * Start the process stream
     * @param stream - The MediaStream  that we will process
     * @param serviceName - The name of service that calls the method (optional, for debug purpose)
     */
    public async startProcessStream(stream: MediaStream, serviceName?: string): Promise<MediaStream> {
        this.logger.info(`[VirtualBackgroundService] startProcessStream from service ${serviceName ? serviceName : 'Unknown'}`);
        return this.setInputStream(stream);
    }

    /**
     * Stop the effect of virtual Background
     */
    public async stopProcessStream(): Promise<void> {
        this.logger.info(`[VirtualBackgroundService] stopProcessStream `);
        this.stream?.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
        this.stream = undefined;
        this.modifiedStream?.getTracks().forEach((track: MediaStreamTrack) => { track.enabled = false; track.stop(); track = null; });
        this.modifiedStream = undefined;
        await this.stopEffect();
    }

    /**
     * Set the type and url of the virtual background
     * @param type - The type of virtual Background ('none", 'image', 'blur', 'blur-plus', 'video')
     * @param url -The url of virtual Background
     */
    public async setBackgroundMode(type: string, url: string = '', stream?: MediaStream): Promise<void> {
        const currentBackgroundType = this.settingsService.getSetting('virtualBackgroundType');
        const currentBackgroundImageUrl = this.settingsService.getSetting('imgVirtualBackground');
        if (type !== currentBackgroundType || ((type === VIRTUAL_BACKGROUND_TYPE.IMAGE || type === VIRTUAL_BACKGROUND_TYPE.VIDEO) && currentBackgroundImageUrl !== url)) {
            this.logger.info(`[VirtualBackgroundService] setBackgroundMode "${type}" ${url ? '"' + url + '"' : ''} `);
            this.settingsService.setSetting('virtualBackgroundType', type);
            this.settingsService.setSetting('imgVirtualBackground', url);
            const modifiedStream = await this.setInputStream(this.stream);
            this.maskFrameTimerWorker.postMessage({ id: SET_TIMEOUT, timeMs: 1000 / 30 });
            if (currentBackgroundType === VIRTUAL_BACKGROUND_TYPE.NONE || type === VIRTUAL_BACKGROUND_TYPE.NONE) {
                this.rxSubject.next(RBEvent.create(VirtualBackgroundServiceEvents.UPDATE_STREAM, { stream: modifiedStream, type }));
            }
        }
    }

    /**
     * Get the type and url od the virtual background
     */
    public getBackgroundMode(): any {
        const type = this.settingsService.getSetting('virtualBackgroundType');
        const url = this.settingsService.getSetting('imgVirtualBackground');
        return { type, url };
    }

    /**
     * Set the stream in mirror mode or not
     * @param mirror - True if the stream is in mirror mode
     */
    public setMirror(mirror: boolean): void {
        this.settingsService.setSetting('virtualBackgroundMirror', mirror ? 'true' : 'false');
        this.isStreamMirrored = mirror;
        this.rxSubject.next(RBEvent.create(VirtualBackgroundServiceEvents.UPDATE_STREAM_MIRROR));
    }

    /**
     * Return true if the stream is in mirror mode
     */
    public getMirror(): boolean {
        return this.isStreamMirrored;
    }

    /**
     * Return true if the calculation of stats is activated
     */
    public enableStats(enabled: boolean): void {
        this.statsEnabledFlag = false; //enabled;
    }

    public setFramerate(framerate: number): void {
        // Do nothing
    }

    /****************************************************************************/
    /** PRIVATE STUFF                                                          */
    /****************************************************************************/

    private webglAvailable() {
        // Create outputCanvas element
        this.cleanUp();
        if (this.outputCanvasElement) { this.outputCanvasElement.remove(); }
        this.outputCanvasElement = document.createElement('canvas');
        this.outputCanvasElement.setAttribute('style', 'display:none');

        this.webglFlag = this.getSetting('webGLAllowed') === 'true';

        if (this.webglFlag) {
            this.gl = this.outputCanvasElement.getContext('webgl2');
            if (!this.gl || !(this.gl instanceof WebGL2RenderingContext)) {
                this.logger.warn('[VirtualBackgroundService] NOT AVAILABLE -- REASON : Failed to get Webgl2 context');
                this.outputCanvasElement.remove();
                this.outputCanvasElement = document.createElement('canvas');
                this.outputCanvasElement.setAttribute('style', 'display:none');
            }
            else this.webglAllowed = true;
        }
        else this.webglAllowed = false;
    }

    /**
     *  Start the effect of Virtual Background
     * @returns {MediaStream} return a MediaStream with a AudioTrack
     */
    private async startEffect(stream: MediaStream, options: Options): Promise<MediaStream | null> {
        try {
            this.options = options;
            this.options.segmentationDimensions = this.simdSupported ? segmentationDimensions.model144 : segmentationDimensions.model96;

            // Initialize framerate stats
            /*if (this.statsEnabledFlag) {
                this.statsInterval = setInterval(() => {
                    this.stats.next(this.stats.value);
                }, 1000);
            }*/

            // Create the webWorker
            this.maskFrameTimerWorker = new Worker(makeTimerWorkerScript(), { name: 'Virtual background effect worker' });
            this.maskFrameTimerWorker.onmessage = this.onMaskFrameTimer;

            // Get input stream constraints
            const firstVideoTrack = stream.getVideoTracks()[0];
            const { height, frameRate, width } = firstVideoTrack.getSettings ? firstVideoTrack.getSettings() : firstVideoTrack.getConstraints();

            // Attach input stream to video element
            this.videoElement.width = width as number;
            this.videoElement.height = height as number;
            this.videoElement.srcObject = stream;
            this.videoElement.onloadeddata = async () => {
                this.videoElement.muted = true;
                await this.videoElement.play();

                this.maskFrameTimerWorker.postMessage({ id: SET_TIMEOUT, timeMs: 1000 / 30 });
            };

            // Capture the output stream from outputCanvas and attach audio track
            this.outputCanvasElement.width = width as number;
            this.outputCanvasElement.height = height as number;

            this.videoPipeline = this.webglAllowed ? Webgl2Pipeline.create(this) : Canvas2DPipeline.create(this);
            await this.videoPipeline.init();

            // Capture the final stream from output canvas and re-attach audio track
            const finalStream: MediaStream = (this.outputCanvasElement as any).captureStream(frameRate as number) as MediaStream;
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length > 0) { finalStream.addTrack(stream.getAudioTracks()[0].clone()); }

            return finalStream;
        }
        catch (error) {
            this.logger.error(`[VirtualBackgroundService] startEffect -- Failure -- ${error.message}`);
            throw error;
        }
    }

    /**
     * @function stopEffect
     * @instance
     * @private
     * @async
     * @description Stop the effect of Virtual Background
     */
    private async stopEffect() {
        if (this.maskFrameTimerWorker) {
            this.maskFrameTimerWorker.postMessage({ id: CLEAR_TIMEOUT });
            this.maskFrameTimerWorker.terminate();
            this.videoElement.pause();
            this.videoElement.srcObject = null;
        }
        if (this.statsInterval) { clearInterval(this.statsInterval); }
    }

    /**
     * @function setInputStream
     * @instance
     * @private
     * @async
     * @param {MediaStream} stream The MediaStream  that we will process
     * @description Initialize the stream and the virtual background
     * @returns {Promise<MediaStream>} return a promise of the modified MediaStream
     */
    private async setInputStream(stream: MediaStream): Promise<MediaStream> {
        const changeBackgroundAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.BLURRING_UI_2_0) && this.settingsService.getSetting('virtualBackgroundFlag') === 'true';
        const backgroundType = changeBackgroundAllowed ? this.settingsService.getSetting('virtualBackgroundType') : VIRTUAL_BACKGROUND_TYPE.NONE;
        const imgBg = this.settingsService.getSetting('imgVirtualBackground');
        this.stream = stream;

        if (backgroundType === VIRTUAL_BACKGROUND_TYPE.NONE || this.stream.getVideoTracks().length === 0) {
            await this.stopEffect();
            this.modifiedStream = this.stream;
        }
        else {
            await this.initVirtualBackground();
            await this.stopEffect();
            try { this.modifiedStream = await this.startEffect(this.stream, { backgroundType, imgBg }); }
            catch (error) {
                this.logger.error(`[VirtualBackgroundService] setInputStream -- Failure -- ${error.message}`);
                await this.stopEffect();
                this.settingsService.setSetting('virtualBackgroundType', VIRTUAL_BACKGROUND_TYPE.NONE);
                this.modifiedStream = this.stream;
            }
        }

        //keep track of the same stream kind
        if (this.stream) { this.modifiedStream["mediaStreamKind"] = this.stream["mediaStreamKind"]; }

        return this.modifiedStream;
    }

    /**
     * @function initVirtualBackground
     * @instance
     * @private
     * @async
     * @description initialize the virtual background according to the supported model
     */
    private async initVirtualBackground(): Promise<void> {
        try {
            if (!this.started) {

                this.started = true;
                this.videoElement.play();
                // solution from: https://github.com/Volcomix/virtual-background/issues/16

                if (this.simdSupported) this.tflite = await createTFLiteSIMDModule({ locateFile: () => '/cacheV2/virtual-background/tflite/tflite-simd.wasm' });
                else this.tflite = await createTFLiteModule({ locateFile: () => '/cacheV2/virtual-background/tflite/tflite.wasm' });

                const modelResponse = await fetch(this.simdSupported ? this.models.model144 : this.models.model96);
                const modelBufferOffset = this.tflite._getModelBufferMemoryOffset();
                if (!modelResponse.ok) { throw new Error('Failed to download tflite model!'); }
                const model = await modelResponse.arrayBuffer();

                this.tflite.HEAPU8.set(new Uint8Array(model), modelBufferOffset);
                this.tflite._loadModel(model.byteLength);
                this.segmentationPixelCount = this.simdSupported
                    ? segmentationDimensions.model144.width * segmentationDimensions.model144.height
                    : segmentationDimensions.model96.width * segmentationDimensions.model96.height;

                this.outputMemoryOffset = this.tflite._getOutputMemoryOffset() / 4;

                // Bind event handler so it is only bound once for every instance.
                this.onMaskFrameTimer = this.onMaskFrameTimer.bind(this);
            }
        }
        catch (error) {
            this.logger.error(`[VirtualBackgroundService] initVirtualBackground-- failure-- ${error.message} `);
        }
    }

    /**
     * @function onMaskFrameTimer
     * @instance
     * @private
     * @param {any} response response
     * @description calls the renderMask function if the id is the expected one
     */
    private onMaskFrameTimer(response: any): any {
        if (response.data.id === TIMEOUT_TICK) { this.render(); }
    }

    /**
     * @function wasmAvailable
     * @instance
     * @private
     * @returns {boolean} return true if webassembly is available
     */
    private wasmAvailable(): boolean {
        return typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function";
    }

    /**
     * @function simdAvailable
     * @instance
     * @private
     * @returns {boolean} return true if simd is available
     */
    private simdAvailable(): boolean {
        // From wasm-check library
        if (!this.wasmAvailable()) return false;
        const u32 = (...bytes) => Uint32Array.of(0x6D736100, 1, ...bytes);
        const u32a = (...bytes) => u32(1610679297, 33751040, ...bytes, 40239360, 259);
        const simdWasm = u32a(84344833, 6357249, 17369600, 4259847, 186257917, 1845758464);
        return WebAssembly.validate(simdWasm);
    }

    /**
     * @function render
     * @private
     * @async
     * @description calls the render function of canvas2D or Webgl2 to create the virtual background
     */
    private async render(): Promise<any> {
        try {

            this.videoPipeline.render();

            this.maskFrameTimerWorker.postMessage({ id: SET_TIMEOUT, timeMs: 1000 / 30 });
        }
        catch (error) {
            this.logger.error(`[VirtualBackgroundService] render-- failure-- ${error.message} `);
        }
    }

    /**
     * @function cleanUp
     * @private
     * @description calls the cleanUp function of the used context
     */
    private cleanUp() {
        if (this.videoPipeline) this.videoPipeline.cleanUp();
        if (this.statsInterval) clearInterval(this.statsInterval);
    }

    /**
     * @function getBackgrounds
     * @instance
     * @private
     * @async
     * @description Asynchronous method to get the user's Background Images
     * @returns {Promise<any[]>} List of the user's virtual Background
     */
    private async getBackgrounds(): Promise<any> {
        try {
            const userId = this.contactService.userContact.dbId;
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${userId}/backgrounds`;
            const headers = this.authService.getRequestHeader();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) throw response;
            return (await response.json()).data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getBackgrounds");
            this.logger.error(`[VirtualBackgroundService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to add user's virtual background on server
     * @param backgroundUser - the background to add
     * @param loadBgUrl - the local path of the background to add
     */
    public async putPersonnalBackgroundImage(backgroundUser: any, loadBgUrl: string): Promise<void> {
        try {
            const userId = this.contactService.userContact.dbId;
            const binaryData = getBinaryData(backgroundUser);
            this.loadBg.value.loadPersonalBg.unshift({ url: loadBgUrl });
            this.loadBg.next(this.loadBg.value);
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${userId}/backgrounds/upload`;
            const headers = this.authService.getPostHeader("image/" + binaryData.type);
            const response: Response = await fetch(url, { method: "POST", headers, body: binaryData.data });
            if (!response.ok) throw response;
            return await response.json();

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "putPersonnalBackgroudImage");
            this.logger.error(`[VirtualBackgroundService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Asynchronous method to delete user's virtual background on server
     * @param backgroundIdUser - the id's background to delete
     */
    public async deletePersonnalBackgroundImage(backgroundIdUser: string): Promise<void> {
        try {
            const userId = this.contactService.userContact.dbId;
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${userId}/backgrounds/${backgroundIdUser}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "DELETE", headers });
            if (!response.ok) throw response;

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "deletePersonnalBackgroundImage");
            this.logger.error(`[VirtualBackgroundService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /****************************************************************************/
    /** ADMIN                                                                  */
    /****************************************************************************/

    /**
     * @function putAdminBackgroundImage
     * @instance
     * @public
     * @async
     * @description Asynchronous method to add company's virtual background on server
     * @param {any} backgroundCompany the background to add
     * @returns {void} Returns the server response
     */
    public async putAdminBackgroundImage(backgroundCompany: any): Promise<void> {
        try {
            const companyId = this.contactService.userContact.company.id;
            const binaryData = getBinaryData(backgroundCompany);
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/companies/${companyId}/backgrounds/upload`;
            const headers = this.authService.getPostHeader("image/" + binaryData.type);
            const response: Response = await fetch(url, { method: "POST", headers, body: binaryData.data });
            if (!response.ok) throw response;
            return await response.json();

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "putAdminBackgroundImage");
            this.logger.error(`[VirtualBackgroundService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * @function deleteAdminBackgroundImage
     * @instance
     * @public
     * @async
     * @description Asynchronous method to delete user's virtual background on server
     * @param {string} backgroundIdAdmin the id's background to delete
     */
    public async deleteAdminBackgroundImage(backgroundIdAdmin: string): Promise<void> {
        try {
            const companyId = this.contactService.userContact.company.id;
            const url = `${config.restServerUrl}/api/rainbow/admin/v1.0/companies/${companyId}/backgrounds/${backgroundIdAdmin}`;
            const headers = this.authService.getPostHeader();
            const response = await fetch(url, { method: "DELETE", headers });
            if (!response.ok) throw response;

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "deleteAdminBackgroundImage");
            this.logger.error(`[VirtualBackgroundService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public setSetting(key: string, value: string): void {
        switch (key) {
            case 'webGLAllowed':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.webglAvailable();
                break;
            case 'showVideoBackground':
                this.settings[key] = value; localStorage.setItem(key, value);
                break;
            case 'sigmaColor':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.setPostProcessingValue();
                if (this.webglAllowed) this.videoPipeline.updatePostProcessingConfig();
                break;

            case 'sigmaSpace':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.setPostProcessingValue();
                if (this.webglAllowed) this.videoPipeline.updatePostProcessingConfig();
                break;

            case 'coverageMax':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.setPostProcessingValue();
                if (this.webglAllowed) this.videoPipeline.updatePostProcessingConfig();
                break;
            case 'coverageMin':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.setPostProcessingValue();
                if (this.webglAllowed) this.videoPipeline.updatePostProcessingConfig();
                break;
            case 'lightWrapping':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.setPostProcessingValue();
                if (this.webglAllowed) this.videoPipeline.updatePostProcessingConfig();
                break;
            case 'blendMode':
                this.settings[key] = value; localStorage.setItem(key, value);
                this.setPostProcessingValue();
                if (this.webglAllowed) this.videoPipeline.updatePostProcessingConfig();
                break;

            default: break;
        }
    }

    public getSetting(key: string): string { return this.settings[key]; }

    public setPostProcessingValue() {
        this.sigmaSpace = parseInt(this.getSetting('sigmaSpace'), 10);
        this.sigmaColor = parseInt(this.getSetting('sigmaColor'), 10);
        this.coverageMax = parseInt(this.getSetting('coverageMax'), 10);
        this.coverageMin = parseInt(this.getSetting('coverageMin'), 10);
        this.lightWrapping = parseInt(this.getSetting('lightWrapping'), 10);
        this.blendMode = parseInt(this.getSetting('blendMode'), 10);
    }

}
